import { Controller } from '@hotwired/stimulus'
import uniqWith from 'lodash/uniqWith'

export default class extends Controller {
  static values = {
    requestId: Number,
    location: Object,

    // 固定検索パラメータ
    facilityGroupIds: Array,
    foodPlanExist: Boolean,
    welfare: Boolean,
    careLevelIds: Array,
    includeHospice: Boolean,
    cityIds: Array,
    residentAddress: String,
    removeNursingCare: Boolean,
    doubleRoom: Boolean,

    // 施設検索用月額料金計算に必要なパラメータ
    currentCareLevel: Number,
    estimatedCareLevel: Number,
    careInsuranceRate: Number,

    selectedFacilities: Array,
    deletedFacilityIds: Array,
  }

  static targets = [
    // googlemap
    'map',

    // 検索条件
    'admissionFeeMin',
    'admissionFeeMax',
    'monthlyFeeMin',
    'monthlyFeeMax',
    'modifiedMonthlyFeeMin',
    'modifiedMonthlyFeeMax',
    'includeFullOccupancy',
    'includeLowMatchingScore',

    // 登録済み施設
    'selectedFacilities',
  ]

  // 介護度定数（APIから取得）
  careLevelConstants = null

  facilityGroups = {}

  get map () {
    if (this._map) return this._map

    return this.initializeMap()
  }

  get viewComponentClassName () {
    return 'viewcomponent::admin::ca::request::condition_matchings::maps::show'
  }

  get header () {
    const element = document.head.querySelector('meta[name="csrf-token"]')

    if (!element || !element.content) {
      console.error('CSRF token not found')
      alert('セキュリティトークンが見つかりません。ページを再読み込みしてください。')
      throw new Error('CSRF token not found')
    }

    return {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': element.content,
    }
  }

  async connect () {
    this.updateSelectedFacilities()

    if (typeof (google) !== 'undefined') {
      this.initializeMap()
      this.fetchFacilities()
    }

    // 施設検索用月額料金の自動計算（既に値が設定されている場合はスキップ）
    const hasInitialValue = this.modifiedMonthlyFeeMinTarget.value || this.modifiedMonthlyFeeMaxTarget.value
    if (!hasInitialValue) {
      await this.calculateModifiedMonthlyFee()
    }
  }

  // Google Mapの初期化を行う
  initializeMap () {
    const map = new google.maps.Map(this.mapTarget, {
      center: new google.maps.LatLng(
        this.locationValue.lat, this.locationValue.lon,
      ),
      zoom: 17,
      fullscreenControl: false,
      mapTypeControl: false,
    })

    this._map = map

    this.infowindow = new google.maps.InfoWindow({ content: '' })
    return this._map
  }

  // 施設情報をAPIから取得し、地図上にマーカーを表示する
  async fetchFacilities () {
    try {
      const url = `/api/admin/ca/requests/${this.requestIdValue}/map_facilities?${this.buildQueryParameter()}`
      const res = await fetch(url, {
        method: 'GET',
        headers: this.header,
      })
      if (!res.ok) throw new Error('APIエラー')
      const data = await res.json()
      this.facilityGroups = data.facility_groups
      this.updateMarker(this.facilityGroups)
    } catch (e) {
      console.error('Failed to fetch company counts:', e)
      alert('データの取得に失敗しました。時間をおいて再度お試しください。')
    }
  }

  // 登録済み施設の表示を更新する
  updateSelectedFacilities () {
    if (this.selectedFacilitiesValue.length === 0) {
      this.selectedFacilitiesTarget.innerHTML = ''
      return
    }

    const list = this.selectedFacilitiesValue.map(
      (facility) => `
      <li
        class="list-group-item"
        data-facility="${facility.id}"
        data-action="mouseover->${this.element.dataset.controller}#registeredFacilityMouseover
        mouseout->${this.element.dataset.controller}#registeredFacilityMouseout"
      >
        <input type="hidden" name="facility_ids[]" value="${facility.id}" />
        <div class="d-flex">
          <a href="javascript:void(0)" data-url="/admin/ca/request/map/facilities/${facility.id}/?request_id=${this.requestIdValue}&condition_matching=true" data-behavior="dialog">
            ${facility.name}
          </a>
          <div class="ml-2">
            ${facility.has_facility_special_unit_price ? '<span class="special-measure-label special-unit-label">特</span>' : ''}
            ${facility.has_facility_campaign ? '<span class="special-measure-label campaign-label">キ</span>' : ''}
          </div>
        </div>
        <div class="d-flex align-items-center">
          <div class="facility-category ${facility.category.show_category_name ? facility.category.slug : ''}">
            <b>${facility.category.show_category_name ? facility.category.name.slice(0, 1) : '他'}</b>
          </div>
          <small class="ml-2">${facility.facility_address}</small>
          <small class="ml-2">月額: <strong class="text-danger">${facility.monthly_price_range}</strong></small>
          <small class="ml-2">成約: <strong class="text-danger">${facility.contract_unit_price}</strong></small>
        </div>
      </li>
      `)

    list.unshift(`
      <li class="list-group-item">
        登録済み施設（カード上部の黄色枠は登録済みを示します）
      </li>
    `)
    this.selectedFacilitiesTarget.innerHTML = list.join('')
  }

  registeredFacilityMouseover (e) {
    e.preventDefault()
    e.stopPropagation()

    if (!this.infowindows) return

    const facilityId = parseInt(e.currentTarget.dataset.facility)
    this.focusRegisterdMarker(facilityId, true)
    this.focusInfowindow(facilityId, true)
    window.location.href = `#facility-${facilityId}`
  }

  registeredFacilityMouseout (e) {
    e.preventDefault()
    e.stopPropagation()

    if (!this.infowindows) return

    const facilityId = parseInt(e.currentTarget.dataset.facility)
    this.focusRegisterdMarker(facilityId, false)
    this.focusInfowindow(facilityId, false)
  }

  focusRegisterdMarker (facilityId, shown) {
    const element = document.getElementById(`marker-facility-${facilityId}`)
    if (!element) return

    if (shown) {
      element.classList.add('focus')
    } else {
      element.classList.remove('focus')
    }
  }

  // 施設グループごとにマーカー・InfoWindowを生成し、地図に表示する
  updateMarker (facilityGroups) {
    // 既存マーカー・infowindowをクリア
    this.markers?.forEach((marker) => marker.setMap(null))
    this.infowindows?.forEach((infowindow) => infowindow.setMap(null))
    this.markers = []
    this.infowindows = []

    Object.entries(facilityGroups).forEach(([groupId, groupData], idx) => {
      groupData.facilities.forEach((facility) => {
        const marker = this.createMarker(facility)
        this.markers.push(marker)

        const classList = this.buildClassList(facility, idx)
        const url = this.buildFacilityUrl(facility.id, groupId)
        const content = this.buildInfoWindowContent(facility, classList, url)

        const infowindow = this.createInfoWindow(facility.id, content)
        infowindow.open(this.map, marker)
        this.addClassToInfoWindows(infowindow, facility.id)
        this.infowindows.push(infowindow)
      })
    })
  }

  // 1施設分のGoogleマーカーを生成する
  createMarker (facility) {
    const marker = new google.maps.Marker({
      visible: false,
      map: this.map,
      facility,
    })
    marker.setPosition({
      lat: parseFloat(facility.address.latitude),
      lng: parseFloat(facility.address.longitude),
    })
    return marker
  }

  // 施設の状態に応じたクラス名を生成する
  buildClassList (facility, idx) {
    let classList = `${this.viewComponentClassName}::maker target facility-group-${idx + 1}`
    if (this.deletedFacilityIdsValue.includes(facility.id)) {
      classList += ' deleted-facility'
    }
    const isRegisteredFacility = this.selectedFacilitiesValue.some((selectedFacility) => selectedFacility.id === facility.id)
    if (isRegisteredFacility) {
      classList += ' registered-facility'
    }
    return classList
  }

  // 施設詳細ダイアログのURLを生成する
  buildFacilityUrl (facilityId, facilityGroupId) {
    return `/admin/ca/request/map/facilities/${facilityId}/?request_id=${this.requestIdValue}&condition_matching=true&facility_group_id=${facilityGroupId}`
  }

  // InfoWindowのHTMLコンテンツを生成する
  buildInfoWindowContent (facility, classList, url) {
    const deletedLabel = this.deletedFacilityIdsValue.includes(facility.id) ? '<span>取消済</span>' : ''
    const specialUnitPriceLabel = facility.special_unit_prices.length ? '<span class="special-measure-label special-unit-label">特単</span>' : ''
    const campaignLabel = facility.campaigns.length ? '<span class="special-measure-label campaign-label">キャンペーン</span>' : ''
    return `
      <div id="infowindow-${facility.id}"
        class="${classList}"
        data-facility="${facility.id}"
        data-action="mouseover->${this.element.dataset.controller}#mouseover mouseout->${this.element.dataset.controller}#mouseout">
        <div class="mb-1">
          ${specialUnitPriceLabel}
          ${campaignLabel}
        </div>
        ${deletedLabel}
        <small>${facility.category.name}</small>
        <a class="d-block text-center" data-url=${url} data-behavior="dialog">${facility.name}</a>
        <div class="ml-1">
          <small>${facility.address.prefecture.name}</small>
          <small>${facility.address.city.name}</small>
        </div>
        <div class="text-muted">
          <small>月額: <strong class="text-danger">${facility.monthly_price_range}</strong></small>
          <br>
          <small>成約: <strong class="text-danger">${facility.contract.contract_fee}</strong></small>
        </div>
      </div>
    `
  }

  // InfoWindowインスタンスを生成する
  createInfoWindow (facilityId, content) {
    return new google.maps.InfoWindow({ facilityId, content, disableAutoPan: true })
  }

  // マーカー上の施設にマウスオーバーした際の処理
  mouseover (e) {
    e.preventDefault()
    e.stopPropagation()
    const facilityId = parseInt(e.currentTarget.dataset.facility)
    this.focusInfowindow(facilityId, true)
  }

  // マーカー上の施設からマウスアウトした際の処理
  mouseout (e) {
    e.preventDefault()
    e.stopPropagation()
    const facilityId = parseInt(e.currentTarget.dataset.facility)
    this.focusInfowindow(facilityId, false)
  }

  // 指定した施設のInfoWindowのZIndexを制御する
  focusInfowindow (facilityId, shown) {
    const infowindow = this.findInfoWindow(facilityId)
    if (!infowindow) return

    if (shown) {
      infowindow.setZIndex(1001)
    } else {
      infowindow.setZIndex(-1)
    }
  }

  // 施設IDからInfoWindowを検索する
  findInfoWindow (facilityId) {
    return this.infowindows.find((infowindow) => facilityId === infowindow.facilityId)
  }

  // InfoWindowのDOMに状態ごとのクラスを付与する
  addClassToInfoWindows (infoWindow, facilityId) {
    google.maps.event.addListenerOnce(infoWindow, 'domready', () => {
      document.querySelectorAll('.gm-style-iw-t').forEach(iwOuter => {
        const infoWindowElement = iwOuter.querySelector(`#infowindow-${facilityId}`)
        if (infoWindowElement) {
          iwOuter.id = `marker-facility-${facilityId}`
        }
        iwOuter.classList.add('searchable')

        // 取消済みラベルのスタイル用のclass追加
        const deletedFacilityElement = iwOuter.querySelector('.deleted-facility')
        if (deletedFacilityElement) {
          iwOuter.classList.add('deleted-label')
        }

        // 登録済み施設のスタイル用のclass追加
        const registeredFacilityElement = iwOuter.querySelector('.registered-facility')
        if (registeredFacilityElement) {
          iwOuter.classList.add('registered')
        }

        // 施設グループごとのスタイル用のclass追加
        const facilityGroup1Element = iwOuter.querySelector('.facility-group-1')
        if (facilityGroup1Element) {
          iwOuter.classList.add('facility-group-1')
        }

        // 施設グループごとのスタイル用のclass追加
        const facilityGroup2Element = iwOuter.querySelector('.facility-group-2')
        if (facilityGroup2Element) {
          iwOuter.classList.add('facility-group-2')
        }
      })
    })
  }

  // 検索条件をクエリパラメータに変換する
  buildQueryParameter () {
    const params = new URLSearchParams()
    Object.entries(this.params()).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // 配列の場合は[]を付けて複数値を追加
        value.forEach(v => params.append(`${key}[]`, v))
      } else if (typeof value === 'object' && value !== null) {
        // オブジェクトの場合はキーごとに値を追加
        Object.entries(value).forEach(([k, v]) => {
          params.append(`${key}[${k}]`, v)
        })
      } else if (typeof value === 'boolean') {
        params.append(key, value ? 'true' : 'false')
      } else if (value !== undefined && value !== null && value !== '') {
        params.append(key, value)
      }
    })

    return params.toString()
  }

  // 検索条件の値をまとめて返す
  params () {
    return {
      admission_fee_min: this.admissionFeeMinTarget.value,
      admission_fee_max: this.admissionFeeMaxTarget.value,
      monthly_fee_min: this.monthlyFeeMinTarget.value,
      monthly_fee_max: this.monthlyFeeMaxTarget.value,
      modified_monthly_fee_min: this.modifiedMonthlyFeeMinTarget.value,
      modified_monthly_fee_max: this.modifiedMonthlyFeeMaxTarget.value,
      include_full_occupancy: this.includeFullOccupancyTarget.checked,
      include_low_matching_score: this.includeLowMatchingScoreTarget.checked,
      food_plan_exist: this.foodPlanExistValue,
      welfare: this.welfareValue,
      include_hospice: this.includeHospiceValue,
      facility_group_ids: this.facilityGroupIdsValue,
      care_level_ids: this.careLevelIdsValue,
      city_ids: this.cityIdsValue,
      resident_address: this.residentAddressValue,
      remove_nursing_care: this.removeNursingCareValue,
      double_room: this.doubleRoomValue,
    }
  }

  // 施設候補から削除するAPI処理
  async delete (e) {
    await this.deleteFacility(e.target.dataset.caRequestFacilityId, e.target.dataset.facilityId, false)
  }

  // Issue #13554: 未送付の紹介カードがある提案施設の解除は、サーバが 409(requiresConfirmation) を返す。
  // 確認ダイアログでユーザーが同意した場合のみ confirmed=true で再送し、送付漏れを防ぐ。
  async deleteFacility (caRequestFacilityId, facilityId, confirmed) {
    let url = `/api/admin/ca/request/facilities/${caRequestFacilityId}?request_id=${this.requestIdValue}&facility_id=${facilityId}`
    if (confirmed) url += '&confirmed=true'

    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.header,
      })

      if (response.status === 409) {
        const body = await response.json()
        if (body.requiresConfirmation && window.confirm(body.message)) {
          await this.deleteFacility(caRequestFacilityId, facilityId, true)
        }
        return
      }

      if (!response.ok) throw new Error(response.statusText)

      const data = await response.json()
      const origin = this.selectedFacilitiesValue
      const list = origin.filter(item => item.id !== data.facilityId)
      this.selectedFacilitiesValue = list

      const newDeletedFacilityIds = [...this.deletedFacilityIdsValue, data.facilityId]
      this.deletedFacilityIdsValue = newDeletedFacilityIds

      this.updateSelectedFacilities()
      this.updateMarker(this.facilityGroups)
    } catch (error) {
      console.log(error)
      alert('施設を削除できませんでした。')
    }
  }

  // 施設候補に追加するAPI処理
  async add (e) {
    const url = '/api/admin/ca/request/facilities'

    await fetch(url, {
      method: 'POST',
      headers: this.header,
      body: JSON.stringify({ request_id: this.requestIdValue, facility_id: e.target.dataset.value, facility_group_id: e.target.dataset.facilityGroupId }),
    })
      .then(response => {
        if (response.ok) return response.json()
        throw new Error(response.statusText)
      })
      .then(data => {
        const origin = this.selectedFacilitiesValue
        origin.push({
          id: Number(e.target.dataset.value),
          name: e.target.dataset.label,
          has_facility_special_unit_price: data.has_facility_special_unit_price,
          has_facility_campaign: data.has_facility_campaign,
          category: data.category,
          facility_address: data.facility_address,
          monthly_price_range: data.monthly_price_range,
          contract_unit_price: data.contract_unit_price,
        })

        const list = uniqWith(origin, (org, oth) => (org.id === oth.id))
        this.selectedFacilitiesValue = list

        this.deletedFacilityIdsValue = this.deletedFacilityIdsValue.filter(facilityId => {
          return facilityId !== data.facilityId
        })

        this.updateSelectedFacilities()
        this.updateMarker(this.facilityGroups)
      })
      .catch(error => {
        console.log(error)
        alert('施設を登録できませんでした。')
      })
  }

  // 施設検索用月額料金の自動計算
  async calculateModifiedMonthlyFee () {
    const monthlyFeeMin = Number(this.monthlyFeeMinTarget.value) || 0
    const monthlyFeeMax = Number(this.monthlyFeeMaxTarget.value) || 0

    // input_paramsから介護度と介護保険自己負担割合を取得（現在の介護度を使用）
    const careLevelId = this.getCurrentCareLevel()
    const careInsuranceRate = this.getCareInsuranceRate()

    if (!careLevelId || !careInsuranceRate) {
      // 介護度または自己負担割合が未設定の場合、元の値をそのまま設定
      this.updateModifiedFeeFields(monthlyFeeMin || '', monthlyFeeMax || '')
      return
    }

    // API呼び出しを試み、失敗した場合はローカル計算にフォールバック
    try {
      await this.calculateModifiedMonthlyFeeViaAPI()
    } catch (error) {
      console.warn('API calculation failed, falling back to local calculation:', error)
      const result = this.calculateModifiedMonthlyFeeLocal(monthlyFeeMin, monthlyFeeMax, careLevelId, careInsuranceRate)
      this.updateModifiedFeeFields(
        result.min === 0 ? '' : result.min,
        result.max === 0 ? '' : result.max,
      )
    }
  }

  // ローカルでの月額料金計算
  calculateModifiedMonthlyFeeLocal (monthlyFeeMin, monthlyFeeMax, careLevelId, careInsuranceRate) {
    const careInsuranceBurden = this.calculateCareInsuranceBurden(careLevelId, careInsuranceRate)

    return {
      min: Math.max(0, monthlyFeeMin - careInsuranceBurden),
      max: Math.max(0, monthlyFeeMax - careInsuranceBurden),
    }
  }

  async calculateModifiedMonthlyFeeViaAPI () {
    const monthlyFeeMin = Number(this.monthlyFeeMinTarget.value) || 0
    const monthlyFeeMax = Number(this.monthlyFeeMaxTarget.value) || 0
    const careLevelId = this.getCurrentCareLevel()
    const careInsuranceRate = this.getCareInsuranceRate()

    if (!careLevelId || !careInsuranceRate) {
      this.updateModifiedFeeFields(monthlyFeeMin || '', monthlyFeeMax || '')
      return
    }

    const url = `/api/admin/ca/requests/${this.requestIdValue}/monthly_fee_calculations`
    const res = await fetch(url, {
      method: 'POST',
      headers: this.header,
      body: JSON.stringify({
        monthly_fee_min: monthlyFeeMin,
        monthly_fee_max: monthlyFeeMax,
        care_level_id: careLevelId,
        care_insurance_rate: careInsuranceRate,
      }),
    })
    if (!res.ok) throw new Error('APIエラー')
    const data = await res.json()

    // APIから定数も取得可能
    if (data.care_level_constants) {
      this.careLevelConstants = data.care_level_constants
    }

    this.updateModifiedFeeFields(
      data.modified_monthly_fee_min === 0 ? '' : data.modified_monthly_fee_min,
      data.modified_monthly_fee_max === 0 ? '' : data.modified_monthly_fee_max,
    )
  }

  // 現在の介護度を取得（住民票住所は都道府県と市区町村から構築）
  getCurrentCareLevel () {
    // input_paramsから現在の介護度を取得
    return this.getCurrentCareLevelFromParams()
  }

  // input_paramsから現在の介護度を取得
  getCurrentCareLevelFromParams () {
    // candidate.care_level_idに相当する値を取得
    // 実際の値はサーバー側から渡される必要があるため、stimulus valuesから取得
    return this.hasCurrentCareLevelValue ? this.currentCareLevelValue : null
  }

  // 介護保険自己負担割合を取得
  getCareInsuranceRate () {
    // candidate.care_insurance_rate_idに相当する値を取得
    return this.hasCareInsuranceRateValue ? this.careInsuranceRateValue : null
  }

  // 介護保険自己負担額の計算（ローカルフォールバック用）
  calculateCareInsuranceBurden (careLevelId, careInsuranceRate) {
    const careLevelNum = Number(careLevelId)

    // 介護度定数（APIから取得した値を使用、なければデフォルト値）
    const CARE_LEVELS = this.careLevelConstants || {
      SUPPORT_1: 11,
      SUPPORT_2: 12,
      NURSING_1: 21,
      NURSING_2: 22,
      NURSING_3: 23,
      NURSING_4: 24,
      NURSING_5: 25,
    }

    const burdenMap = {
      light: { 1: 1, 2: 2, 3: 2 }, // 要支援1〜要介護1
      medium: { 1: 3, 2: 5, 3: 7 }, // 要介護2〜3
      heavy: { 1: 5, 2: 8, 3: 10 }, // 要介護4〜5
    }

    let category
    if ([CARE_LEVELS.SUPPORT_1, CARE_LEVELS.SUPPORT_2, CARE_LEVELS.NURSING_1].includes(careLevelNum)) {
      category = 'light'
    } else if ([CARE_LEVELS.NURSING_2, CARE_LEVELS.NURSING_3].includes(careLevelNum)) {
      category = 'medium'
    } else if ([CARE_LEVELS.NURSING_4, CARE_LEVELS.NURSING_5].includes(careLevelNum) || careLevelNum > 25) {
      category = 'heavy'
    } else {
      return 0 // 不明な介護度
    }

    return burdenMap[category][careInsuranceRate] || 0
  }

  // 施設検索用月額料金フィールドの更新
  updateModifiedFeeFields (minValue, maxValue) {
    this.modifiedMonthlyFeeMinTarget.value = minValue
    this.modifiedMonthlyFeeMaxTarget.value = maxValue
  }
}
